import { useEffect, useSyncExternalStore } from "react";

/**
 * Small browser-state hooks for screens that hold a live code on a device:
 * whether the page is visible, whether the browser thinks it is online, and a
 * best-effort screen wake lock while a code is on screen.
 */

function subscribeVisibility(listener: () => void): () => void {
  document.addEventListener("visibilitychange", listener);
  return () => document.removeEventListener("visibilitychange", listener);
}

function readVisible(): boolean {
  return document.visibilityState !== "hidden";
}

/** True while the document is visible (not a background tab or locked screen). */
export function usePageVisible(): boolean {
  return useSyncExternalStore(subscribeVisibility, readVisible, () => true);
}

function subscribeOnline(listener: () => void): () => void {
  window.addEventListener("online", listener);
  window.addEventListener("offline", listener);
  return () => {
    window.removeEventListener("online", listener);
    window.removeEventListener("offline", listener);
  };
}

function readOnline(): boolean {
  return typeof navigator === "undefined" || navigator.onLine !== false;
}

/** The browser's own online signal; a failed request is the other half. */
export function useOnline(): boolean {
  return useSyncExternalStore(subscribeOnline, readOnline, () => true);
}

interface WakeLockSentinelLike {
  release: () => Promise<void>;
}

/**
 * Keep the screen awake while `active` (a code is showing, a kiosk is
 * scanning). Browsers release the lock when the page is hidden, so it is
 * requested again on every visible change. Unsupported browsers are a no-op.
 */
export function useScreenWakeLock(active: boolean): void {
  const visible = usePageVisible();
  useEffect(() => {
    const wakeLock = (
      navigator as Navigator & {
        wakeLock?: { request: (type: "screen") => Promise<WakeLockSentinelLike> };
      }
    ).wakeLock;
    if (!active || !visible || !wakeLock) return;
    let sentinel: WakeLockSentinelLike | null = null;
    let released = false;
    wakeLock
      .request("screen")
      .then((lock) => {
        if (released) void lock.release().catch(() => undefined);
        else sentinel = lock;
      })
      .catch(() => undefined);
    return () => {
      released = true;
      void sentinel?.release().catch(() => undefined);
    };
  }, [active, visible]);
}
